import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import ClientSidebar from './ClientSidebar';
import FeedbackModal from './FeedbackModal';
import LoginPopupModal from '../common/LoginPopupModal';
import ConsentModal from '../common/ConsentModal';
import SpotlightTour from '../onboarding/SpotlightTour';
import TourPrompt from '../onboarding/TourPrompt';
import { hasCompletedTour, resetTour, TOUR_KEY } from '../../hooks/useTour';
import { useIdleLogout } from '../../hooks/useIdleLogout';
import { Toaster } from '../ui/sonner';
import axios from 'axios';
import { toast } from 'sonner';
import { Menu, MessageSquare } from 'lucide-react';
import PartnerMark from '../PartnerMark';
import { clearTokens, clearClientIdentity } from '../../utils/authTokens';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientLayout = ({ subdomainFromHost, isEmbedded = false, embedPartnerId = null, onLogout: externalLogout = null, children = null }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showTourPrompt, setShowTourPrompt] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [tourPromptDismissedThisSession, setTourPromptDismissedThisSession] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { subdomain: subdomainFromParams, partnerId: partnerIdFromParams } = useParams();
  
  // For embedded mode, get subdomain from localStorage
  const subdomain = isEmbedded 
    ? localStorage.getItem('clientSubdomain') 
    : (subdomainFromHost || subdomainFromParams);
  
  const partnerId = embedPartnerId || partnerIdFromParams;

  // Get login URL based on routing mode
  const getLoginUrl = () => {
    if (isEmbedded && partnerId) {
      return `/embed/${partnerId}/client`;
    }
    return '/login';
  };

  // Check consent status
  useEffect(() => {
    const checkConsent = async () => {
      const token = localStorage.getItem('clientToken');
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/client/consent-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const { consent_given, consent_required } = response.data;
        
        if (consent_required && !consent_given) {
          setShowConsentModal(true);
        } else {
          setConsentChecked(true);
        }
      } catch (error) {
        console.error('Failed to check consent status:', error);
        setConsentChecked(true); // Allow access if check fails
      }
    };
    
    checkConsent();
  }, []);

  const handleConsentGiven = () => {
    setShowConsentModal(false);
    setConsentChecked(true);
  };

  // Check if user is authenticated and fetch partner info.
  // IMPORTANT: the dashboard is rendered ONLY after the stored token is
  // validated against the backend. A stale/expired token left in localStorage
  // (e.g. user closed the tab without logging out, then returned — possibly on
  // a shared browser) must NOT render a dashboard seeded with a previous
  // session's data ("ghost / old wills"). If the session is dead and cannot be
  // refreshed, we wipe ALL client keys and send the user to a clean login.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('clientToken');
    const storedSubdomain = localStorage.getItem('clientSubdomain');

    // If no token, redirect to login immediately.
    if (!token) {
      navigate(getLoginUrl());
      return undefined;
    }

    // If token exists but subdomain mismatch, update stored subdomain.
    if (subdomain && storedSubdomain !== subdomain) {
      localStorage.setItem('clientSubdomain', subdomain);
    }

    const validateSession = async () => {
      try {
        const profileRes = await axios.get(`${API}/client/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        // Sync the displayed identity to the account that is ACTUALLY
        // authenticated, so the UI can never show a name/email that disagrees
        // with the session the backend will scope data to.
        const c = profileRes.data || {};
        if (c.id) localStorage.setItem('clientId', c.id);
        if (c.name) localStorage.setItem('clientName', c.name);
        if (c.email) localStorage.setItem('clientEmail', c.email);
        setAuthChecking(false);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          // Session is dead (and the refresh attempt by the axios interceptor
          // also failed). Wipe everything and go to a clean login.
          clearTokens('client');
          clearClientIdentity();
          navigate(getLoginUrl());
          return;
        }
        // Transient/network error — don't lock the user out of their own data.
        setAuthChecking(false);
      }
    };

    // Fetch partner info for logo (independent of session validity).
    const fetchPartnerInfo = async () => {
      try {
        const response = await axios.get(`${API}/subdomain/${subdomain || storedSubdomain}`);
        if (!cancelled) setPartnerInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch partner info:', error);
      }
    };

    validateSession();
    fetchPartnerInfo();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location, subdomain]);

  // Note: Mobile sidebar is closed via onMobileClose callback in navigation clicks

  // Close mobile sidebar on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show login popup on first load (after successful login)
  useEffect(() => {
    const token = localStorage.getItem('clientToken');
    const justLoggedIn = sessionStorage.getItem('client_just_logged_in');
    
    if (token && justLoggedIn) {
      sessionStorage.removeItem('client_just_logged_in');
      setShowLoginPopup(true);
    }
  }, []);

  // Onboarding tour trigger — only on /dashboard, after popup + consent close, with 1.5s delay
  // Only auto-shows if user hasn't completed the tour before.
  useEffect(() => {
    const isDashboardRoute = location.pathname === '/dashboard' || location.pathname.endsWith('/dashboard');
    if (!isDashboardRoute) {
      setShowTourPrompt(false);
      return undefined;
    }
    if (showLoginPopup || showConsentModal || !consentChecked) return undefined;
    if (hasCompletedTour()) return undefined;
    if (showTour || tourPromptDismissedThisSession) return undefined;

    const t = setTimeout(() => setShowTourPrompt(true), 1500);
    return () => clearTimeout(t);
  }, [location.pathname, showLoginPopup, showConsentModal, consentChecked, showTour, tourPromptDismissedThisSession]);

  const handleStartTour = useCallback(() => {
    setShowTourPrompt(false);
    // Reset completion flag if user manually triggers it
    resetTour();
    // Ensure we're on dashboard route for the tour
    if (!(location.pathname === '/dashboard' || location.pathname.endsWith('/dashboard'))) {
      navigate('/dashboard');
    }
    // Mobile sidebar state is managed per step via handleTourStepChange.
    setTimeout(() => setShowTour(true), 120);
  }, [location.pathname, navigate]);

  const handleCloseTour = useCallback(() => {
    setShowTour(false);
    // Close mobile sidebar after tour finishes
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsMobileSidebarOpen(false);
    }
    try { localStorage.setItem(TOUR_KEY, '1'); } catch (_) { /* ignore */ }
  }, []);

  const handleDismissTourPrompt = useCallback(() => {
    setShowTourPrompt(false);
    setTourPromptDismissedThisSession(true);
    // Mark as completed so it doesn't auto-appear again. User can restart from sidebar.
    try { localStorage.setItem(TOUR_KEY, '1'); } catch (_) { /* ignore */ }
  }, []);

  const handleTourStepChange = useCallback((stepIndex, step) => {
    // On mobile, open/close the sidebar per step so the spotlight target is visible.
    if (typeof window === 'undefined' || window.innerWidth >= 1024) return;
    setIsMobileSidebarOpen(step?.mobileSidebar === 'open');
  }, []);

  // Tour steps definition — 5 cinematic stops on the client dashboard.
  // `mobileSidebar` controls the mobile sidebar state for each step.
  const tourSteps = [
    {
      target: 'create-will',
      selector: '[data-tour-id="tour-create-will"]',
      title: 'Create a Will',
      description: 'Create and securely manage your digital Will with guided workflows.',
      mobileSidebar: 'closed',
    },
    {
      target: 'life-vault',
      selector: '[data-tour-id="tour-life-vault"]',
      title: 'Life Vault',
      description: 'Store critical family, financial and legal documents securely in one place.',
      mobileSidebar: 'open',
    },
    {
      target: 'sharing',
      selector: '[data-tour-id="tour-profile"]',
      title: 'Sharing',
      description: 'Control who can access important information during emergencies — manage trusted contacts and partner sharing inside Profile.',
      mobileSidebar: 'open',
    },
    {
      target: 'profile',
      selector: '[data-tour-id="tour-profile"]',
      title: 'Profile',
      description: 'Manage your personal information, access invoices and consents centrally.',
      mobileSidebar: 'open',
    },
    {
      target: 'feedback',
      selector: '[data-tour-id="tour-feedback"]',
      title: 'Share Feedback',
      description: 'Have ideas or run into something? Drop us a quick note anytime from here.',
      mobileSidebar: 'closed',
    },
  ];

  const handleLogout = async () => {
    const token = localStorage.getItem('clientToken');
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) { /* ignore */ }
    }
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientAccessToken');
    localStorage.removeItem('clientRefreshToken');
    localStorage.removeItem('clientEmail');
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientName');
    localStorage.removeItem('clientSubdomain');
    localStorage.removeItem('isEmbedded');
    
    if (externalLogout) {
      externalLogout();
    } else {
      navigate(getLoginUrl());
    }
  };

  // Idle auto-logout — clears the session after 30 min of inactivity. Important
  // hygiene for shared/family devices so an abandoned tab can't keep serving a
  // client's data. Only armed once the session is validated.
  useIdleLogout({
    enabled: !authChecking,
    timeoutMs: 30 * 60 * 1000,
    onIdle: () => {
      toast.info('Signed out due to inactivity. Please log in again.');
      handleLogout();
    },
  });

  // Cross-tab logout sync — if the client token is cleared in another tab
  // (logout / idle / session-revoked), redirect this tab to login too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'clientToken' && !e.newValue) {
        navigate(getLoginUrl());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      {authChecking ? (
        <div
          className="min-h-screen flex items-center justify-center"
          data-testid="client-session-loading"
        >
          <div className="flex flex-col items-center gap-3 text-slate-400">
            <div className="w-8 h-8 border-2 border-amber-500/40 border-t-amber-500 rounded-full animate-spin" />
            <span className="text-sm">Loading your dashboard…</span>
          </div>
        </div>
      ) : (
      <>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 border-b border-slate-800 flex items-center gap-3 px-4 z-40">
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="p-2 -ml-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors flex-shrink-0"
          aria-label="Open menu"
          data-testid="client-mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex-1 min-w-0 flex items-center">
          <PartnerMark
            partnerInfo={partnerInfo}
            size="sm"
            showName={true}
            nameClassName="text-sm"
          />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <ClientSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onLogout={handleLogout}
        partnerInfo={partnerInfo}
        subdomain={subdomain}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        useSubdomainRouting={!!subdomainFromHost}
        isEmbedded={isEmbedded}
        embedPartnerId={partnerId}
        onStartTour={handleStartTour}
      />

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-0 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="p-4 sm:p-6 lg:p-8">
          {children || (
            <Outlet context={{ 
              partnerInfo, 
              subdomain, 
              useSubdomainRouting: !!subdomainFromHost,
              isEmbedded,
              embedPartnerId: partnerId
            }} />
          )}
        </div>
      </main>

      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsFeedbackOpen(true)}
        className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-full shadow-lg shadow-amber-500/30 transition-all hover:scale-105 z-30"
        data-testid="feedback-button"
        data-tour-id="tour-feedback"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={isFeedbackOpen} 
        onClose={() => setIsFeedbackOpen(false)} 
      />

      {/* Login Popup Modal */}
      {showLoginPopup && (
        <LoginPopupModal 
          type="client" 
          onClose={() => setShowLoginPopup(false)} 
        />
      )}

      {/* Consent Modal - Blocks access until consent is given */}
      {showConsentModal && (
        <ConsentModal
          userType="client"
          token={localStorage.getItem('clientToken')}
          onConsentGiven={handleConsentGiven}
        />
      )}

      {/* Onboarding — subtle prompt then full spotlight tour */}
      <TourPrompt
        isOpen={showTourPrompt}
        onAccept={handleStartTour}
        onDismiss={handleDismissTourPrompt}
      />
      <SpotlightTour
        isOpen={showTour}
        steps={tourSteps}
        onClose={handleCloseTour}
        onStepChange={handleTourStepChange}
      />

      <Toaster position="top-right" richColors />
      </>
      )}
    </div>
  );
};

export default ClientLayout;
